<template>
  <HeadlessListbox v-model="modelValue" as="div" multiple>
    <HeadlessListboxLabel
      v-if="label"
      class="mb-1 block text-left text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </HeadlessListboxLabel>

    <div ref="listboxReferenceRef">
      <HeadlessListboxButton as="template">
        <slot name="button">
          <button
            type="button"
            :class="[
              'flex w-full flex-nowrap items-center justify-between rounded-md border px-3 py-2 text-sm font-semibold focus:outline-none sm:text-sm',
              hasError
                ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
                : 'focus:border-primary focus:ring-primary',
            ]"
          >
            <span class="mr-2 line-clamp-1">
              {{ selectedLabel }}
            </span>
            <IconChevronDown
              class="rounded-full text-background-fg/40"
            />
          </button>
        </slot>
      </HeadlessListboxButton>
    </div>

    <Teleport to="body">
      <div ref="listboxOptionsRef" :style="floatingStyles">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessListboxOptions
            class="mb-6 max-h-60 w-full overflow-y-auto rounded-lg bg-background shadow"
          >
            <HeadlessListboxOption
              v-for="option in options"
              :key="option.value ?? undefined"
              #default="{ selected }"
              :value="option.value"
            >
              <div
                :class="[
                  selected
                    ? 'bg-background-fg/5'
                    : 'text-background-fg/50 hover:bg-background-fg/1',
                  'flex cursor-pointer items-center justify-start px-3 py-2',
                ]"
              >
                <div
                  :class="[
                    'mr-2 flex size-5 items-center justify-around rounded',
                    option.value !== null &&
                    option.value !== undefined
                      ? 'border border-background-fg/70'
                      : '',
                  ]"
                >
                  <IconCheck
                    v-if="selected"
                    class="text-primary"
                  />
                </div>

                <component
                  :is="option.icon"
                  v-if="option.icon"
                  class="mr-2"
                />
                <span
                  class="line-clamp-1 text-left text-sm font-semibold"
                >
                  {{ option.label }}
                </span>
              </div>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </Transition>
      </div>
    </Teleport>
  </HeadlessListbox>
</template>

<script
  setup
  lang="ts"
  generic="
    TValue extends string | number | undefined | null
  "
>
import {
  useFloating,
  offset,
  flip,
  autoUpdate,
  size,
} from "@floating-ui/vue"

const props = defineProps<{
  options: ReadonlyArray<Option<TValue>>
  label?: string
  errorMessage?: string
  name?: string
}>()

const hasError = computed(() => !!props.errorMessage)

const modelValue = defineModel<Array<TValue>>({
  required: true,
})

const selectedLabel = computed(() =>
  modelValue.value
    ?.map(
      (selectedValue) =>
        props.options.find(
          ({ value }) => value === selectedValue
        )?.label
    )
    .join(", ")
)

const listboxOptionsRef = useTemplateRef(
  "listboxOptionsRef"
)
const listboxReferenceRef = useTemplateRef(
  "listboxReferenceRef"
)

const { floatingStyles } = useFloating(
  listboxReferenceRef,
  listboxOptionsRef,

  {
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
  }
)
</script>
